import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCheck, Clock3, BadgeCheck, XCircle } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { api, inr, fmtDate, fmtErr } from "@/lib/api";
import { CopyButton } from "@/components/CopyButton";

export default function AdminWithdrawals() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");

  const load = (f = filter) => api.get(`/admin/withdrawals${f ? `?status=${f}` : ""}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(""); }, []);

  const update = async (w, status) => {
    let note = "";
    if (status === "rejected") {
      note = window.prompt("Reason for rejection (required):", "");
      if (!note || !note.trim()) { toast.error("Rejection reason is required"); return; }
    }
    try {
      await api.post(`/admin/withdrawals/${w.id}/status`, { status, note });
      toast.success(`Withdrawal marked ${status}`);
      await load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  return (
    <div data-testid="admin-withdrawals-page">
      <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900">Withdrawal Management</h1>
      <p className="mt-1 text-sm text-slate-600">Process user payout requests to their UPI IDs.</p>

      <div className="mt-5 flex gap-2 flex-wrap" data-testid="admin-withdrawal-filters">
        {[["", "All"], ["pending", "Pending"], ["processing", "Processing"], ["paid", "Paid"], ["rejected", "Rejected"]].map(([v, label]) => (
          <button key={v} data-testid={`withdrawal-filter-${v || "all"}`} onClick={() => { setFilter(v); setItems(null); load(v); }}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${filter === v ? "bg-blue-600 text-white shadow-sm" : "bg-white text-slate-700 border border-slate-200 hover:bg-slate-50"}`}>
            {label}
          </button>
        ))}
      </div>

      {!items ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
      ) : (
        <div className="mt-6 bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="admin-withdrawals-table">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/60 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Withdrawal ID</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">UPI ID</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Requested</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((w) => (
                <tr key={w.id} data-testid={`withdrawal-row-${w.withdrawal_code}`}>
                  <td className="px-4 py-3.5 font-medium text-slate-900">{w.withdrawal_code}</td>
                  <td className="px-4 py-3.5"><p className="text-slate-800">{w.user_name}</p><p className="text-xs text-slate-500">{w.user_code}</p></td>
                  <td className="px-4 py-3.5 text-slate-700"><span className="inline-flex items-center gap-0.5">{w.upi_id}<CopyButton value={w.upi_id} testid={`copy-upi-${w.withdrawal_code}`} /></span></td>
                  <td className="px-4 py-3.5 font-semibold text-blue-700">{inr(w.amount)}</td>
                  <td className="px-4 py-3.5 text-slate-600 whitespace-nowrap">{fmtDate(w.requested_at)}</td>
                  <td className="px-4 py-3.5">
                    <StatusBadge status={w.status} />
                    {w.admin_note && <p className="text-[10px] text-red-500 mt-1 max-w-[140px] truncate" title={w.admin_note}>{w.admin_note}</p>}
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex justify-end gap-1.5">
                      {w.status === "pending" && (
                        <button onClick={() => update(w, "processing")} data-testid={`approve-withdrawal-${w.withdrawal_code}`} title="Approve (move to processing)" className="p-2 rounded-lg text-sky-600 hover:bg-sky-50"><Clock3 className="w-4 h-4" /></button>
                      )}
                      {(w.status === "pending" || w.status === "processing") && (
                        <>
                          <button onClick={() => update(w, "paid")} data-testid={`mark-paid-withdrawal-${w.withdrawal_code}`} title="Mark paid" className="p-2 rounded-lg text-green-600 hover:bg-green-50"><BadgeCheck className="w-4 h-4" /></button>
                          <button onClick={() => update(w, "rejected")} data-testid={`reject-withdrawal-${w.withdrawal_code}`} title="Reject" className="p-2 rounded-lg text-red-600 hover:bg-red-50"><XCircle className="w-4 h-4" /></button>
                        </>
                      )}
                      {w.status === "paid" && <CheckCheck className="w-4 h-4 text-green-600 m-2" />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <p className="p-8 text-center text-sm text-slate-500" data-testid="withdrawals-empty">No withdrawal requests found.</p>}
        </div>
      )}
    </div>
  );
}
